import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Boxes, PackageMinus, Trash2, Plus } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { WarehouseAddForm } from "../components/WarehouseAddForm";
import { WarehouseMovesDialog } from "../components/WarehouseMovesDialog";

export default function Warehouse() {
  const { t, fmt } = useI18n();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [issue, setIssue] = useState(null);

  const load = () => api.get("/warehouse", { params: { q: q || undefined } }).then((r) => setItems(r.data));
  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const doIssue = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/warehouse/${issue.item.id}/issue`, { quantity: Number(issue.qty), note: issue.note });
      toast.success(t("success")); setIssue(null); load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (it) => {
    if (!window.confirm(`${t("confirmDeleteItem")}\n${it.name}`)) return;
    try { await api.delete(`/warehouse/${it.id}`); load(); } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-4xl sm:text-5xl font-extrabold">{t("warehouse")}</h1>
      <div className="grid lg:grid-cols-[360px_1fr] gap-6 items-start">
        <WarehouseAddForm onDone={load} />
        <div className="space-y-3">
          <Input className="max-w-sm bg-white" placeholder={t("whSearch")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="wh-search-input" />
          <div className="bg-white border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader><TableRow>{["partName", "partNumber", "quantity", "supplier", "date", "actions"].map((h) => <TableHead key={h} className="text-start">{t(h)}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-10">{t("noData")}</TableCell></TableRow>}
                {items.map((it) => (
                  <TableRow key={it.id} data-testid={`wh-row-${it.id}`}>
                    <TableCell className="font-bold">{it.name}</TableCell>
                    <TableCell className="font-mono text-sm" dir="ltr">{it.part_number || "-"}</TableCell>
                    <TableCell><span data-testid={`wh-qty-${it.id}`} className={`inline-block min-w-8 text-center rounded-full font-bold px-2 ${it.quantity > 0 ? "bg-emerald-100 text-emerald-900" : "bg-rose-100 text-rose-900"}`}>{it.quantity}</span></TableCell>
                    <TableCell>{it.supplier_name || "-"}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{fmt(it.updated_at)}</TableCell>
                    <TableCell className="flex flex-wrap gap-1">
                      <WarehouseMovesDialog item={it} />
                      <Button size="sm" variant="outline" disabled={it.quantity < 1} data-testid={`wh-issue-${it.id}`} onClick={() => setIssue({ item: it, qty: 1, note: "" })}><PackageMinus className="w-4 h-4" />{t("whIssue")}</Button>
                      {user.role === "admin" && <Button size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50" data-testid={`wh-delete-${it.id}`} onClick={() => remove(it)}><Trash2 className="w-4 h-4" /></Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {issue && (
            <form onSubmit={doIssue} className="bg-amber-50 border-2 border-[#F5BE25] rounded-lg p-4 flex flex-wrap items-end gap-3" data-testid="wh-issue-form">
              <div className="font-bold w-full flex items-center gap-2"><Boxes className="w-4 h-4" />{t("whIssue")}: {issue.item.name} ({t("whAvailable")} {issue.item.quantity})</div>
              <div className="space-y-1.5"><Label>{t("quantity")}</Label><Input type="number" min={1} max={issue.item.quantity} className="w-24 bg-white" value={issue.qty} onChange={(e) => setIssue({ ...issue, qty: e.target.value })} data-testid="wh-issue-qty" required /></div>
              <div className="space-y-1.5 flex-1 min-w-[160px]"><Label>{t("note")}</Label><Input className="bg-white" value={issue.note} onChange={(e) => setIssue({ ...issue, note: e.target.value })} data-testid="wh-issue-note" /></div>
              <Button type="button" variant="outline" onClick={() => setIssue(null)}>{t("cancel")}</Button>
              <Button type="submit" data-testid="wh-issue-submit"><Plus className="w-4 h-4 hidden" />{t("whIssue")}</Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
